/**
 * Shared, de-duplicated access to the signed-in Catalyst user.
 *
 * getCurrentProjectUser() hits /baas/v1/project/.../project-user/current, and
 * it was being called from four independent places — main.js during boot,
 * HomeViewSection/Header.vue on mount, App.vue, and AccountingTrainingView.vue.
 * On a normal page load at least two of them fired, producing the same request
 * twice (measured at 1.69s and again at 3.89s) for an answer that cannot change
 * within a page view.
 *
 * This memoises both the in-flight promise and the settled result, so however
 * many components ask, exactly one request is made:
 *
 *   - first caller  -> starts the request, gets the promise
 *   - callers while it is in flight -> get the SAME promise, no second request
 *   - callers after it resolves     -> get the cached value, no request at all
 *
 * Failures are deliberately not cached. A rejection usually means "not signed
 * in yet", and callers redirect to /login on it; caching that would make the
 * failure permanent for the rest of the page's life.
 */

let inflight = null;
let resolved = null;

/**
 * @returns {Promise<object>} the Catalyst current-project-user payload
 */
export function getCurrentProjectUser() {
   if (resolved) return Promise.resolve(resolved);
   if (inflight) return inflight;

   const userManagement = window.catalyst.userManagement;

   inflight = userManagement
      .getCurrentProjectUser()
      .then((user) => {
         resolved = user;
         inflight = null;
         return user;
      })
      .catch((err) => {
         // Let the next caller retry rather than inheriting this failure.
         inflight = null;
         throw err;
      });

   return inflight;
}

/**
 * Drop the cached user. Call after sign-in or sign-out, when the answer really
 * has changed.
 */
export function resetCurrentProjectUser() {
   inflight = null;
   resolved = null;
}
